/**
 * Контракты номенклатуры: отрасли и группы (типы) конвейеров (E04.1).
 * Управление ведёт модератор контента без изменения кода (ФТ-3.5, ФТ-8.2).
 */

import { z } from "zod";
import { apiSuccessSchema } from "./envelope";

/** Коды ошибок справочников номенклатуры. */
export const CATALOG_ERROR_CODES = {
  NOT_FOUND: "CATALOG_NOT_FOUND",
  /** Наименование уже занято в пределах справочника. */
  NAME_TAKEN: "CATALOG_NAME_TAKEN",
  /** Запись используется другими сущностями и не может быть удалена. */
  IN_USE: "CATALOG_IN_USE",
  /** Модель нельзя опубликовать: нет методики расчёта или ни одной отрасли. */
  NOT_PUBLISHABLE: "CATALOG_NOT_PUBLISHABLE",
  /** Ссылка на несуществующую группу, методику, отрасль или единицу измерения. */
  BAD_REFERENCE: "CATALOG_BAD_REFERENCE",
  /** Тип значения атрибута не согласован со списком значений. */
  INVALID_VALUE_LIST: "CATALOG_INVALID_VALUE_LIST",
  /** Тип ввода не согласован с формулой или флагом ручного ввода сверх списка. */
  INVALID_INPUT_TYPE: "CATALOG_INVALID_INPUT_TYPE",
  /** Атрибут уже добавлен в схему этой модели. */
  ALREADY_IN_SCHEMA: "CATALOG_ALREADY_IN_SCHEMA",
  /** Ключ строки справочника не соответствует типу таблицы (range/category/multi). */
  INVALID_REFERENCE_KEY: "CATALOG_INVALID_REFERENCE_KEY",
  /** Пересчёт формульных констант типоразмера завершился ошибкой. */
  CALCULATION_FAILED: "CATALOG_CALCULATION_FAILED",
  /** Версию матрицы со статусом rejected нельзя активировать. */
  MATRIX_NOT_VALIDATED: "CATALOG_MATRIX_NOT_VALIDATED",
} as const;

export type CatalogErrorCode = (typeof CATALOG_ERROR_CODES)[keyof typeof CATALOG_ERROR_CODES];

/** Статус публикации записи справочника. */
export const publishStatusSchema = z.enum(["draft", "published"]);

export type PublishStatus = z.infer<typeof publishStatusSchema>;

const nameSchema = z.string().trim().min(1).max(255);
const sortOrderSchema = z.number().int().min(0).max(100_000);

/**
 * Десятичное число в виде строки: коэффициенты хранятся как `numeric`,
 * через float их передавать нельзя — теряется точность (НФТ-12, разд. 10 ЧТЗ).
 */
export const decimalStringSchema = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,8})?$/, "Ожидается неотрицательное десятичное число");

/** Отрасль применения (шаг 1 пошагового интерфейса). */
export const industrySchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  sortOrder: sortOrderSchema,
  status: publishStatusSchema,
  /** Сколько моделей привязано к отрасли — видно, что затронет удаление. */
  modelCount: z.number().int().nonnegative(),
});

export type Industry = z.infer<typeof industrySchema>;

/** Тело `POST /api/v1/admin/industries`. */
export const createIndustryRequestSchema = z.object({
  name: nameSchema,
  sortOrder: sortOrderSchema.default(0),
  status: publishStatusSchema.default("draft"),
});

export type CreateIndustryRequest = z.infer<typeof createIndustryRequestSchema>;

/** Тело `PATCH /api/v1/admin/industries/{id}`. */
export const updateIndustryRequestSchema = z
  .object({
    name: nameSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    status: publishStatusSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: "Нет полей для изменения" });

export type UpdateIndustryRequest = z.infer<typeof updateIndustryRequestSchema>;

/** Группа (тип) конвейеров. */
export const conveyorGroupSchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  sortOrder: sortOrderSchema,
  /** Групповой коэффициент цены (ФТ-10.3); правится отдельным разрешением. */
  priceCoefficient: decimalStringSchema,
  modelCount: z.number().int().nonnegative(),
});

export type ConveyorGroup = z.infer<typeof conveyorGroupSchema>;

/** Тело `POST /api/v1/admin/groups`. */
export const createConveyorGroupRequestSchema = z.object({
  name: nameSchema,
  sortOrder: sortOrderSchema.default(0),
  priceCoefficient: decimalStringSchema.default("1"),
});

export type CreateConveyorGroupRequest = z.infer<typeof createConveyorGroupRequestSchema>;

/** Тело `PATCH /api/v1/admin/groups/{id}`. */
export const updateConveyorGroupRequestSchema = z
  .object({
    name: nameSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    priceCoefficient: decimalStringSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: "Нет полей для изменения" });

export type UpdateConveyorGroupRequest = z.infer<typeof updateConveyorGroupRequestSchema>;

/**
 * Модель (семейство) конвейеров: определяет методику расчёта и схему атрибутов
 * (ФТ-3.2, ФТ-3.2.1). Конкретные типоразмеры — в `conveyor_units` (E04.4).
 */
export const conveyorModelSchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  groupId: z.string().uuid(),
  groupName: nameSchema,
  /** Методика расчёта; без неё модель нельзя опубликовать. */
  methodologyId: z.string().uuid().nullable(),
  methodologyName: nameSchema.nullable(),
  /** Отображаемое исполнение по умолчанию; рабочее исполнение — атрибут (Архитектура 7.1). */
  defaultExecution: z.string().trim().max(128).nullable(),
  status: publishStatusSchema,
  /** Отрасли применения, связь «многие-ко-многим». */
  industryIds: z.array(z.string().uuid()),
  /** Типоразмеры и атрибуты модели — их наличие блокирует удаление. */
  unitCount: z.number().int().nonnegative(),
  attributeCount: z.number().int().nonnegative(),
});

export type ConveyorModel = z.infer<typeof conveyorModelSchema>;

const modelFieldsSchema = z.object({
  name: nameSchema,
  groupId: z.string().uuid(),
  methodologyId: z.string().uuid().nullable().default(null),
  defaultExecution: z.string().trim().max(128).nullable().default(null),
  status: publishStatusSchema.default("draft"),
  industryIds: z.array(z.string().uuid()).default([]),
});

/** Тело `POST /api/v1/admin/models`. */
export const createConveyorModelRequestSchema = modelFieldsSchema;

export type CreateConveyorModelRequest = z.infer<typeof createConveyorModelRequestSchema>;

/** Тело `PATCH /api/v1/admin/models/{id}`. */
export const updateConveyorModelRequestSchema = modelFieldsSchema
  .partial()
  .refine((body) => Object.keys(body).length > 0, { message: "Нет полей для изменения" });

export type UpdateConveyorModelRequest = z.infer<typeof updateConveyorModelRequestSchema>;

/** Методика расчёта в списке выбора; ведение методик — эпик E06. */
export const methodologySummarySchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  versionNo: z.number().int().positive(),
  isActive: z.boolean(),
});

export type MethodologySummary = z.infer<typeof methodologySummarySchema>;

/** Конверты ответов. */
export const conveyorModelListResponseSchema = apiSuccessSchema(z.array(conveyorModelSchema));
export const conveyorModelResponseSchema = apiSuccessSchema(conveyorModelSchema);
export const methodologyListResponseSchema = apiSuccessSchema(z.array(methodologySummarySchema));
export const industryListResponseSchema = apiSuccessSchema(z.array(industrySchema));
export const industryResponseSchema = apiSuccessSchema(industrySchema);
export const conveyorGroupListResponseSchema = apiSuccessSchema(z.array(conveyorGroupSchema));
export const conveyorGroupResponseSchema = apiSuccessSchema(conveyorGroupSchema);

/** Полезная нагрузка удаления записи справочника. */
export const deleteResultSchema = z.object({ deleted: z.boolean() });

export type DeleteResult = z.infer<typeof deleteResultSchema>;
